import {
  ExclamationTriangleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import type {
  SubscriptionTier,
  SubscriptionTierInfo,
} from '@/types/subscription';
import { TIER_FALLBACK, getNextTier } from '@/types/subscription';

interface LimitBannerProps {
  remaining: number;
  limit: number;
  tier: SubscriptionTier;
  nextTierInfo?: SubscriptionTierInfo;
  onUpgrade?: () => void;
  onDismiss?: () => void;
  className?: string;
}

export default function LimitBanner({
  remaining,
  limit,
  tier,
  nextTierInfo,
  onUpgrade,
  onDismiss,
  className = '',
}: LimitBannerProps) {
  // Don't show for unlimited
  if (limit === -1) return null;

  // Don't show if plenty of room (>30% remaining)
  const percentUsed = ((limit - remaining) / limit) * 100;
  if (percentUsed < 70) return null;

  const isAtLimit = remaining <= 0;
  const tierName = TIER_FALLBACK[tier]?.name ?? tier;
  const nextTier = getNextTier(tier);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className={`
          rounded-lg p-4 flex items-start gap-3
          ${isAtLimit ? 'bg-red-50 border border-red-200' : 'bg-yellow-50 border border-yellow-200'}
          ${className}
        `}
      >
        <ExclamationTriangleIcon
          className={`h-5 w-5 flex-shrink-0 ${isAtLimit ? 'text-red-500' : 'text-yellow-500'}`}
        />

        <div className="flex-1">
          {isAtLimit ? (
            <>
              <h3 className="text-sm font-medium text-red-800">
                Employee limit reached
              </h3>
              <p className="text-sm text-red-700 mt-1">
                You've reached your limit of {limit} employees on the {tierName}{' '}
                plan.
                {nextTierInfo && (
                  <>
                    {' '}
                    Upgrade to {nextTierInfo.name} for up to{' '}
                    {nextTierInfo.employee_limit === -1
                      ? 'unlimited'
                      : nextTierInfo.employee_limit}{' '}
                    employees.
                  </>
                )}
              </p>
            </>
          ) : (
            <>
              <h3 className="text-sm font-medium text-yellow-800">
                Approaching employee limit
              </h3>
              <p className="text-sm text-yellow-700 mt-1">
                You have {remaining} employee{' '}
                {remaining === 1 ? 'slot' : 'slots'} remaining on the {tierName}{' '}
                plan.
              </p>
            </>
          )}

          {nextTier && onUpgrade && (
            <button
              onClick={onUpgrade}
              className={`
                mt-2 text-sm font-medium underline
                ${isAtLimit ? 'text-red-700 hover:text-red-800' : 'text-yellow-700 hover:text-yellow-800'}
              `}
            >
              Upgrade to {nextTierInfo?.name ?? nextTier} →
            </button>
          )}
        </div>

        {onDismiss && !isAtLimit && (
          <button
            onClick={onDismiss}
            className="text-yellow-500 hover:text-yellow-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
